import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Stack,
  Title,
  Card,
  Button,
  Group,
  Alert,
  Text,
  Badge,
  Skeleton,
  SimpleGrid,
  ActionIcon,
  Paper,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconRefresh,
  IconClock,
  IconUsers,
  IconChevronLeft,
  IconFlame,
  IconCheck,
  IconX,
  IconInfoCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useAppUser } from '../hooks/useAppUser';
import { usePageTitle } from '../hooks/usePageTitle';
import { RecipeImage } from '../components/UI/RecipeImage';
import { MoreRestrictionsMenu } from '../components/UI/MoreRestrictionsMenu';
import { RecipeBadge, splitBadgesForDisplay } from '../utils/recipeBadges';
import { TrendingRibbon, TopPickBadge } from '../components/UI/TopPickRibbon';
import { CuisineBadge } from '../components/UI/CuisineBadge';
import { formatTime } from '../utils/formatTime';
import {
  fetchTrendingRecipes,
  refreshTrendingRecipes,
  TrendingSnapshot,
} from '../services/adminTrendingService';

const difficultyColor = (d?: string) =>
  d === 'easy' ? 'green' : d === 'medium' ? 'yellow' : 'red';

const formatAge = (iso: string | null) => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const formatDay = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '-';

export const TrendingBrowserPage = () => {
  usePageTitle('Trending | Meals You Love');
  const { user, loading: userLoading } = useAppUser();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<TrendingSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    // Admin-gated endpoint, so a non-admin request can only ever come back 403.
    // The render guard below shows them the denial without the round trip.
    if (!user?.isAdmin) return;
    setLoading(true);
    try {
      setSnapshot(await fetchTrendingRecipes(user));
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load trending recipes',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Runs the same scan Cloud Scheduler calls nightly, then reloads so the page
  // shows what was actually published rather than the counts we just got back.
  const handleRefresh = async () => {
    if (!user) return;
    setRefreshing(true);
    try {
      const result = await refreshTrendingRecipes(user);
      notifications.show({
        title: 'Trending recomputed',
        message:
          `Scanned ${result.scannedEvents} cook events, ` +
          `${result.resolvedToCatalog} resolved to ${result.distinctCatalogRecipes} catalog recipes, ` +
          `published ${result.published}.`,
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 6000,
      });
      await load();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to refresh trending recipes',
        color: 'red',
        icon: <IconX size={18} />,
        autoClose: 5000,
      });
    } finally {
      setRefreshing(false);
    }
  };

  const isLoading = userLoading || loading;

  if (!userLoading && !user?.isAdmin) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size="1rem" />} title="Access Denied" color="red" radius="lg">
          You do not have admin privileges.
        </Alert>
      </Container>
    );
  }

  const entries = snapshot?.recipes ?? [];
  const neverRan = !isLoading && snapshot?.computedAt === null;
  // Every entry backed by a single household means the ranking is ordering by
  // recency, not popularity. Worth saying out loud rather than letting the
  // scores imply a consensus that isn't there.
  const allSingleHousehold =
    entries.length > 0 && entries.every((e) => e.cookCount <= 1);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Group justify="space-between" align="flex-start">
            {/* nowrap, or the title stack drops below the chevron when the
                subtitle + Recompute button exceed one line */}
            <Group wrap="nowrap">
              <ActionIcon variant="subtle" size="lg" onClick={() => navigate(-1)}>
                <IconChevronLeft size={20} />
              </ActionIcon>
              <Stack gap={0}>
                <Title order={2}>Trending Recipes</Title>
                <Text c="dimmed" size="sm">
                  What households are cooking, recomputed nightly from cook events
                </Text>
              </Stack>
            </Group>
            <Button
              leftSection={<IconRefresh size="1rem" />}
              variant="outline"
              loading={refreshing}
              onClick={handleRefresh}
            >
              Recompute now
            </Button>
          </Group>
        </div>

        <Paper p="md" radius="lg" withBorder>
          <Group gap="xl">
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Last computed</Text>
              <Text size="sm">{isLoading ? '-' : formatAge(snapshot?.computedAt ?? null)}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Published</Text>
              <Text size="sm">{isLoading ? '-' : entries.length}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Events scanned</Text>
              <Text size="sm">{isLoading ? '-' : snapshot?.scannedEvents ?? 0}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Window</Text>
              <Text size="sm">{isLoading ? '-' : `${snapshot?.windowDays ?? '-'} days`}</Text>
            </Stack>
            <Stack gap={2}>
              <Group gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Min households</Text>
                <Tooltip
                  label="A recipe needs this many distinct households before it can trend"
                  multiline
                  w={220}
                >
                  <IconInfoCircle size={13} style={{ opacity: 0.5 }} />
                </Tooltip>
              </Group>
              <Text size="sm">{isLoading ? '-' : snapshot?.minDistinctCooks ?? '-'}</Text>
            </Stack>
          </Group>
        </Paper>

        {allSingleHousehold && (
          <Alert icon={<IconInfoCircle size="1rem" />} color="yellow" radius="lg">
            Every recipe below was cooked by exactly one household, so this list is
            ordering by recency rather than popularity. That resolves on its own as
            more households log cooks.
          </Alert>
        )}

        {isLoading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} padding="lg" radius="lg" withBorder>
                <Card.Section>
                  <Skeleton height={200} radius={0} />
                </Card.Section>
                <Stack gap="xs" mt="md">
                  <Skeleton height={18} width="80%" />
                  <Skeleton height={14} width="95%" />
                  <Group gap="md">
                    <Skeleton height={20} width={70} radius="xl" />
                    <Skeleton height={20} width={55} radius="xl" />
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        ) : neverRan ? (
          <Alert icon={<IconAlertCircle size="1rem" />} title="Never computed" color="blue" radius="lg">
            The nightly scan has not run yet, so nothing has been published. Use
            Recompute now to build the first list.
          </Alert>
        ) : entries.length === 0 ? (
          <Alert icon={<IconFlame size="1rem" />} title="Nothing trending" color="blue" radius="lg">
            The scan ran but published nothing. Either no cook events landed in the
            window, or none of them resolved to catalog recipes.
          </Alert>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {entries.map((entry) => (
              <Card
                key={entry.catalogRecipeId}
                padding="lg"
                radius="lg"
                withBorder
                style={{ cursor: entry.recipe ? 'pointer' : 'default' }}
                onClick={() =>
                  entry.recipe && navigate(`/admin/catalog-recipe/${entry.catalogRecipeId}`)
                }
              >
                <Card.Section style={{ position: 'relative' }}>
                  <RecipeImage
                    src={entry.recipe?.imageUrl}
                    alt={entry.name}
                    height={200}
                  />
                  {entry.recipe?.cuisine && (
                    <CuisineBadge
                      cuisine={entry.recipe.cuisine}
                      placement="overlay"
                      style={{ position: 'absolute', top: 8, left: 8 }}
                    />
                  )}
                  <TrendingRibbon />
                  <Badge
                    size="lg"
                    circle
                    variant="filled"
                    color="dark"
                    style={{ position: 'absolute', bottom: 8, left: 8, opacity: 0.85 }}
                  >
                    {entry.rank}
                  </Badge>
                </Card.Section>

                <Stack gap="xs" mt="md" style={{ flex: 1, justifyContent: 'space-between' }}>
                  <Stack gap="xs">
                    <Group gap="xs" justify="space-between" wrap="nowrap" align="flex-start">
                      <Text fw={500} lineClamp={2}>
                        {entry.name}
                      </Text>
                      {/* Trending replaces Top pick on user-facing cards, so
                          surface the underlying flag here instead. Admins need
                          to know a recipe is both. */}
                      {entry.recipe?.isCurated && <TopPickBadge />}
                    </Group>
                    {entry.recipe?.description && (
                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {entry.recipe.description}
                      </Text>
                    )}
                    {!entry.recipe && (
                      <Text size="sm" c="red">
                        Deleted from the catalog since this list was published
                      </Text>
                    )}
                  </Stack>

                  <Stack gap="xs">
                    <Group gap="md">
                      <Tooltip label="Recency-weighted score, the ordering key">
                        <Badge size="sm" variant="light" color="red">
                          {entry.score.toFixed(3)}
                        </Badge>
                      </Tooltip>
                      <Tooltip label="Distinct households that cooked it in the window">
                        <Badge
                          size="sm"
                          variant="light"
                          color={entry.cookCount > 1 ? 'green' : 'gray'}
                          leftSection={<IconUsers size={12} />}
                        >
                          {entry.cookCount}
                        </Badge>
                      </Tooltip>
                      <Tooltip label="Most recent cook">
                        <Text size="sm" c="dimmed">{formatDay(entry.lastCookedAt)}</Text>
                      </Tooltip>
                    </Group>

                    {entry.recipe && (
                      <>
                        <Group gap="md">
                          {/* Both come straight off the catalog doc, so a
                              malformed recipe can leave them undefined. Drop
                              the row rather than rendering "0 min". */}
                          {entry.recipe.totalTimeMinutes !== undefined && (
                            <Group gap="xs">
                              <IconClock size={16} />
                              <Text size="sm">
                                {formatTime(entry.recipe.totalTimeMinutes)}
                              </Text>
                            </Group>
                          )}
                          {entry.recipe.servings !== undefined && (
                            <Group gap="xs">
                              <IconUsers size={16} />
                              <Text size="sm">{entry.recipe.servings}</Text>
                            </Group>
                          )}
                          {entry.recipe.difficulty && (
                            <Badge
                              size="sm"
                              variant="light"
                              color={difficultyColor(entry.recipe.difficulty)}
                            >
                              {entry.recipe.difficulty}
                            </Badge>
                          )}
                        </Group>

                        <Group gap="xs">
                          {(() => {
                            const allBadges: RecipeBadge[] = [
                              ...entry.recipe.dietaryRestrictions.map((r) => ({
                                text: r,
                                type: 'dietary' as const,
                              })),
                              ...entry.recipe.tags.map((t) => ({
                                text: t,
                                type: 'tag' as const,
                              })),
                            ];
                            const { visible, hiddenRestrictions } = splitBadgesForDisplay(
                              allBadges,
                              3
                            );
                            return (
                              <>
                                {visible.map((badge) => (
                                  <Badge
                                    key={`${badge.type}-${badge.text}`}
                                    size="sm"
                                    variant="outline"
                                  >
                                    {badge.text}
                                  </Badge>
                                ))}
                                <MoreRestrictionsMenu restrictions={hiddenRestrictions} />
                              </>
                            );
                          })()}
                        </Group>
                      </>
                    )}
                  </Stack>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Container>
  );
};
